import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-preact';

import { Link } from '@client/components';

/*----------------------------------
- TYPES
----------------------------------*/

export interface SidebarSubItem {
    id: string;
    label: string;
    href: string;
    active?: boolean;
    icon?: React.ReactNode;
    /** Small colored dot (e.g. "wip"/"todo" progress indicator). */
    statusDot?: 'wip' | 'todo';
}

export interface SidebarItemProps {
    id: string;
    label: string;
    icon?: React.ReactNode;
    href?: string;
    active?: boolean;
    badge?: React.ReactNode;
    /** Presence of items turns this into a collapsible group instead of a plain link. */
    items?: SidebarSubItem[];
    defaultOpen?: boolean;
    collapsed?: boolean;
    /** Controlled expand state — omit to let the item manage its own open/closed state. */
    open?: boolean;
    onToggleOpen?: () => void;
    'data-testid'?: string;
}

export interface SidebarUserMenuAction {
    key: string;
    label: string;
    icon?: React.ReactNode;
    destructive?: boolean;
    separator?: boolean;
}

/*----------------------------------
- ROOT / LAYOUT
----------------------------------*/

export const Sidebar: React.FC<{ collapsed?: boolean; className?: string; children?: React.ReactNode }> = ({
    collapsed,
    className,
    children,
}) => (
    <aside
        className={[
            'flex h-full flex-col bg-linear-to-b from-white via-orange-50/25 to-white transition-[width] duration-300 ease-in-out',
            collapsed ? 'w-19' : 'w-66',
            className,
        ].filter(Boolean).join(' ')}
    >
        {children}
    </aside>
);

export const SidebarCollapseToggle: React.FC<{ collapsed: boolean; onToggle: () => void; className?: string }> = ({
    collapsed,
    onToggle,
    className,
}) => (
    <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={[
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-primary-400 transition-all duration-200 hover:bg-primary-50 hover:text-primary-700 hover:shadow-sm',
            className,
        ].filter(Boolean).join(' ')}
    >
        {collapsed ? <PanelLeftOpen className="h-4.5 w-4.5" strokeWidth={1.5} /> : <PanelLeftClose className="h-4.5 w-4.5" strokeWidth={1.5} />}
    </button>
);

/*----------------------------------
- BRAND
----------------------------------*/

export const SidebarBrand: React.FC<{
    logo: React.ReactNode;
    name: string;
    subtitle?: string;
    collapsed?: boolean;
    'data-testid'?: string;
}> = ({ logo, name, subtitle, collapsed, 'data-testid': testId }) => (
    <div className="group/brand flex w-full items-center gap-3 px-2 py-2">
        {/* The mark itself is already a complete, self-contained tile (logo.svg draws its own
            black rounded-square background and white glyph) — no extra colored wrapper needed,
            just enough depth to sit crisply on the sidebar's pale gradient. */}
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-[0_6px_16px_rgba(15,23,42,0.16)] ring-1 ring-black/[0.06] transition-transform duration-300 ease-out group-hover/brand:scale-105">
            {logo}
        </span>
        {!collapsed && (
            <span className="min-w-0 flex-1 overflow-hidden">
                <span
                    className="block truncate text-[23px] font-black leading-none tracking-tight text-slate-950"
                    data-testid={testId}
                >
                    {name}
                </span>
                {subtitle ? (
                    <span className="mt-1.5 block truncate text-[10px] font-semibold uppercase leading-none tracking-[0.18em] text-slate-400">
                        {subtitle}
                    </span>
                ) : (
                    /* Thin brand-gradient keel under the wordmark — quiet, but unmistakably "the brand corner" */
                    <span className="mt-2 block h-[3px] w-11 rounded-full bg-gradient-primary opacity-70 transition-all duration-300 group-hover/brand:w-16 group-hover/brand:opacity-100" />
                )}
            </span>
        )}
    </div>
);

/*----------------------------------
- GROUP
----------------------------------*/

export const SidebarGroupLabel: React.FC<{ children: React.ReactNode; collapsed?: boolean }> = ({ children, collapsed }) => {
    if (collapsed) return <div className="my-2 h-px bg-slate-100" />;
    return (
        <span className="mb-1 mt-4 block px-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 first:mt-0">
            {children}
        </span>
    );
};

export const SidebarGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="flex flex-col gap-0.5">{children}</div>
);

/*----------------------------------
- ITEM (plain link or collapsible group), styled after a shadcn-style
  "Dashboard Sidebar" reference (chevron-rotate + grid-rows accordion).
----------------------------------*/

export const SidebarItem: React.FC<SidebarItemProps> = ({
    label,
    icon,
    href,
    active,
    badge,
    items,
    defaultOpen = false,
    collapsed,
    open: controlledOpen,
    onToggleOpen,
    'data-testid': testId,
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen || Boolean(items?.some((item) => item.active)));
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;
    const toggleOpen = () => (isControlled ? onToggleOpen?.() : setUncontrolledOpen((v) => !v));
    const isCollapsible = Boolean(items && items.length > 0);

    const rowClasses = [
        'group flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.75 text-[13px] font-medium transition-colors duration-200 select-none',
        active
            ? 'bg-primary-50 text-primary-700 font-semibold shadow-sm ring-1 ring-primary-100'
            : 'text-slate-600 hover:bg-primary-50/70 hover:text-primary-700 hover:ring-1 hover:ring-primary-100/70',
    ].join(' ');

    const row = (
        <>
            <span className="flex min-w-0 items-center gap-2.5">
                {icon && (
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4 ${active ? 'text-primary-600' : 'text-slate-400 group-hover:text-slate-600'}`}>
                        {icon}
                    </span>
                )}
                {!collapsed && <span className="min-w-0 truncate tracking-wide">{label}</span>}
            </span>
            {!collapsed && (
                <span className="flex shrink-0 items-center gap-2">
                    {badge}
                    {isCollapsible && (
                        <ChevronRight
                            className={`h-3.5 w-3.5 shrink-0 text-slate-300 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
                            strokeWidth={2}
                        />
                    )}
                </span>
            )}
        </>
    );

    return (
        <div className="flex w-full flex-col">
            {isCollapsible ? (
                <button type="button" onClick={toggleOpen} className={rowClasses} title={collapsed ? label : undefined} data-testid={testId}>
                    {row}
                </button>
            ) : (
                <Link to={href ?? '#'} className={rowClasses} title={collapsed ? label : undefined} data-testid={testId}>
                    {row}
                </Link>
            )}

            {isCollapsible && !collapsed && (
                <div
                    className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
                        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                >
                    <div className="relative flex min-h-0 flex-col gap-0.5 overflow-hidden pt-0.5">
                        <div className="absolute bottom-0 top-0.5 left-4.25 border-l border-primary-100/80" />
                        {items!.map((sub) => (
                            <Link
                                key={sub.id}
                                to={sub.href}
                                className={[
                                    'flex items-center gap-2.5 truncate rounded-md py-1.75 pl-9 pr-2.5 text-[13px] font-medium transition-colors',
                                    sub.active ? 'text-primary-700 font-semibold' : 'text-slate-500 hover:bg-primary-50/70 hover:text-primary-700',
                                ].join(' ')}
                            >
                                {sub.icon && (
                                    <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center [&>svg]:h-3.5 [&>svg]:w-3.5">
                                        {sub.icon}
                                    </span>
                                )}
                                <span className="min-w-0 flex-1 truncate">{sub.label}</span>
                                {sub.statusDot && (
                                    <span
                                        title={sub.statusDot}
                                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${sub.statusDot === 'todo' ? 'bg-red-500' : 'bg-amber-400'}`}
                                    />
                                )}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/*----------------------------------
- USER FOOTER
----------------------------------*/

export const SidebarUserFooter: React.FC<{
    name: string;
    email?: string;
    avatar?: React.ReactNode;
    collapsed?: boolean;
    actions: SidebarUserMenuAction[];
    onAction: (key: string) => void;
}> = ({ name, email, avatar, collapsed, actions, onAction }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (event: PointerEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    return (
        <div ref={ref} className="relative">
            {open && (
                <div className="absolute bottom-full left-0 z-50 mb-2 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white py-1.5 shadow-[0_16px_40px_rgba(15,23,42,0.16)]">
                    <div className="flex items-center gap-3 border-b border-slate-100 px-3.5 py-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
                            {avatar}
                        </span>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">{name}</p>
                            {email && <p className="truncate text-xs text-slate-400">{email}</p>}
                        </div>
                    </div>
                    {actions.map((action, idx) => (
                        <React.Fragment key={action.key}>
                            {action.separator && idx > 0 && <div className="mx-3 my-1 border-t border-slate-100" />}
                            <button
                                type="button"
                                onClick={() => { onAction(action.key); setOpen(false); }}
                                className={[
                                    'flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm font-medium transition-colors',
                                    action.destructive
                                        ? 'text-rose-600 hover:bg-rose-50'
                                        : 'text-slate-700 hover:bg-slate-50',
                                ].join(' ')}
                            >
                                {action.icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{action.icon}</span>}
                                {action.label}
                            </button>
                        </React.Fragment>
                    ))}
                </div>
            )}

            <button
                type="button"
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors hover:bg-primary-50"
            >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
                    {avatar}
                </span>
                {!collapsed && (
                    <>
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-semibold text-slate-900">{name}</span>
                            {email && <span className="block truncate text-xs text-slate-400">{email}</span>}
                        </span>
                        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-300 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} strokeWidth={2} />
                    </>
                )}
            </button>
        </div>
    );
};
